import React, { useEffect, useState } from "react";

import { ThemeProvider, useTheme } from "styled-components";

import Content from "../content/Content";
import Settings from "../drawers/settings/Settings";
import Templates from "../drawers/templates/Templates";
import Header from "../header/Header";
import Sidebar from "../sidebar/Sidebar";
import AdvancedState from "../../../components/advanced-state/AdvancedState";
import EditDisabled from "../../../components/edit-disabled/EditDisabled";
import WarningModal from "./WarningModal";
import useWidgetSettingsByKey from "../../../hooks/use-widget-settings-by-key";
import { useFetchLibraryMacros } from "../../../hooks/use-fetch-library-macros";
import { useMacroDataQuery } from "../../../hooks/use-macro-data-query";
import { useStyleguideUserPermission } from "../../../hooks/use-styleguide-userpermission";
import { macroStores } from "../../../stores/export-stores";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { handleStoredData } from "./handlers/handle-stored-data";
import { handleSaveMacro, saveAndClose } from "./handlers/save-macro";
import {
  EditorContainer,
  EditorContentContainer,
  EditorMainContainer,
} from "./styles";
import { useStyleguideSetup } from "../../../hooks/use-styleguide-setup";
import { MACRO_KEY, WARNING_DIALOGUE } from "../../defaults";
import {
  ADVANCED_MACRO_KEYS,
  MACROS_REQUIRE_COSMOS,
  MACROS_WITHOUT_SETTINGS,
  MACROS_WITHOUT_SIDEBAR,
} from "../sidebar/defaults";
import { checkForWarnings } from "./handlers/warning-modal";
import { getOwnColors } from "../../../components/ui/color-palettes-picker/colors";
import { useMigrationStore } from "../../../stores/use-migration-store";
import { isMigrationFromDc } from "../../../helpers/dc-to-cloud";
import { checkCosmosInstallation } from "../../../helpers/get-cosmos-installed";
import { WIDGET_COSMOS_IDS } from "./defaults";
import { useTranslation } from "react-i18next";

const Editor = ({ editorMacro }) => {
  const [contentLoaded, setContentLoaded] = useState(false);

  const theme = useTheme();
  const { t } = useTranslation();

  const {
    drawers,
    setDrawers,
    macro,
    setMacro,
    setHasStyleguidePermission,
    libraryMacrosSetters,
    setStyleguideSetup,
    advancedMacroEmptyState,
    isEditingOldMacro,
    setIsEditingOldMacro,
    isWarningModalOpen,
    setIsWarningModalOpen,
    colorPalette,
    setColorPalette,
    isCosmosInstalled,
    setIsCosmosInstalled,
  } = useOpticsStore((state) => ({
    drawers: state.drawers,
    setDrawers: state.setDrawers,
    macro: state.macro,
    setMacro: state.setMacro,
    setHasStyleguidePermission: state.setHasStyleguidePermission,
    libraryMacrosSetters: {
      setLibraryButtons: state.setLibraryButtons,
      setLibraryPanels: state.setLibraryPanels,
      setLibraryCards: state.setLibraryCards,
    },
    styleguideSetup: state.styleguideSetup,
    setStyleguideSetup: state.setStyleguideSetup,
    advancedMacroEmptyState: state.advancedMacroEmptyState,
    isEditingOldMacro: state.isEditingOldMacro,
    setIsEditingOldMacro: state.setIsEditingOldMacro,
    isWarningModalOpen: state.isWarningModalOpen,
    setIsWarningModalOpen: state.setIsWarningModalOpen,
    colorPalette: state.colorPalette,
    setColorPalette: state.setColorPalette,
    isCosmosInstalled: state.isCosmosInstalled,
    setIsCosmosInstalled: state.setIsCosmosInstalled,
  }));

  const { setIsMigrationActive, setOldMacroData } = useMigrationStore();

  const { macroDataSetter, getData, resetStore } = macroStores[macro]?.(
    (state) => ({
      macroDataSetter: state?.setData,
      getData: state?.getData,
      resetStore: state?.resetStore,
    })
  );
  const {
    data: opticsData,
    isLoading: isLoadingOpticsData,
    isFetching: isFetchingOpticsData,
  } = useMacroDataQuery();

  const { loading, styleguideSetup } = useStyleguideSetup();

  useStyleguideUserPermission({ setter: setHasStyleguidePermission });
  useFetchLibraryMacros({ setters: libraryMacrosSetters });

  const { isWidgetSettingsLoading, widgetSettingsData } =
    useWidgetSettingsByKey({
      enabled: isCosmosInstalled && MACROS_REQUIRE_COSMOS.includes(macro),
      key: macro,
      widgetId: WIDGET_COSMOS_IDS[macro],
    });

  const {
    exists = false,
    widget: { selectedField: selectedDatabaseFieldId = "" } = {},
  } = widgetSettingsData;

  const cosmosNoSetup =
    (!exists || !selectedDatabaseFieldId.length) && !isWidgetSettingsLoading;

  const macroData = macroStores[macro]?.((state) => state?.data || {});
  const hasMacroData = Array.isArray(macroData)
    ? !!macroData.length
    : !!Object.keys(macroData).length;

  useEffect(() => {
    if (ADVANCED_MACRO_KEYS.includes(macro)) {
      if (hasMacroData) {
        setDrawers({ templates: false, settings: true });
      } else {
        setDrawers({ templates: true, settings: false });
      }
    }
  }, [hasMacroData, macro]);

  useEffect(() => {
    if (!loading && Object.keys(styleguideSetup || {}).length) {
      setStyleguideSetup(styleguideSetup);

      const ownColors = getOwnColors({
        ownColors: styleguideSetup?.selectedColors,
      });
      if (ownColors && !colorPalette) {
        setColorPalette(ownColors[0]);
      }
    }
  }, [styleguideSetup, setStyleguideSetup, loading]);

  useEffect(() => {
    if (isLoadingOpticsData || isFetchingOpticsData) return;

    const macroKey = opticsData?.macro || editorMacro || MACRO_KEY.OVERVIEW_KEY;
    setMacro({ key: macroKey });
  }, [
    editorMacro,
    opticsData,
    setMacro,
    isLoadingOpticsData,
    isFetchingOpticsData,
  ]);

  useEffect(() => {
    if (contentLoaded || !macro) return;

    if (macro && opticsData) {
      const { content, isOldMacro } = handleStoredData(macro, opticsData);
      if (typeof macroDataSetter === "function" && content) {
        macroDataSetter(content);
        setContentLoaded(true);
      }
      setIsEditingOldMacro(isOldMacro);
    }
  }, [macro, opticsData, setIsEditingOldMacro, macroDataSetter, contentLoaded]);

  useEffect(() => {
    (async () => {
      setIsCosmosInstalled(await checkCosmosInstallation());
    })();
  }, []);

  const hasWarningDialogue = {
    EMPTY_LINK: WARNING_DIALOGUE.EMPTY_LINK.includes(macro || editorMacro),
  };

  const handleOnClick = () => {
    if (hasMacroData) {
      const dataToSave = getData();
      const data = handleSaveMacro(macro, dataToSave);
      if (hasWarningDialogue.EMPTY_LINK && !isWarningModalOpen) {
        const hasWarnings = checkForWarnings({ macro, data: dataToSave });

        if (hasWarnings) {
          setIsWarningModalOpen(true);
        } else {
          saveAndClose({ data });
        }
      } else {
        saveAndClose({ data });
      }
    }
  };

  const showTemplates =
    !!macro && !advancedMacroEmptyState && macro !== MACRO_KEY.OVERVIEW_KEY;

  const showSidebar = !MACROS_WITHOUT_SIDEBAR(t).some(
    (independentMacro) => independentMacro.key === editorMacro
  );

  const settingsConditions = {
    optics:
      !MACROS_WITHOUT_SETTINGS(t).some(
        (noSidebar) => noSidebar.key === macro
      ) &&
      drawers.settings &&
      !advancedMacroEmptyState &&
      !MACROS_REQUIRE_COSMOS.includes(macro),
    cosmos:
      MACROS_REQUIRE_COSMOS.includes(macro) &&
      isCosmosInstalled &&
      !cosmosNoSetup,
  };

  const showSettings = settingsConditions.optics || settingsConditions.cosmos;

  if (isEditingOldMacro)
    return (
      <EditDisabled
        macro={macro}
        handleClickOnRebuild={(macro) => {
          resetStore();

          setIsMigrationActive(true);
          if (isMigrationFromDc(opticsData)) {
            const { content } = handleStoredData(macro, opticsData);
            setOldMacroData({
              macro,
              data: content,
            });
          } else {
            setOldMacroData({ macro, data: opticsData });
          }

          setDrawers({ templates: true, settings: false });
          setIsEditingOldMacro(false);
        }}
      />
    );

  return (
    <ThemeProvider theme={theme}>
      <EditorContainer>
        <Header onClick={handleOnClick} hasSave={hasMacroData} />
        <EditorMainContainer>
          {showSidebar && <Sidebar />}
          {showTemplates && <Templates />}
          <EditorContentContainer>
            {advancedMacroEmptyState ? <AdvancedState /> : <Content />}
          </EditorContentContainer>

          {showSettings && <Settings />}
        </EditorMainContainer>
      </EditorContainer>

      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        onConfirm={handleOnClick}
        macroName={macro || editorMacro}
      />
    </ThemeProvider>
  );
};

export default Editor;
